import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [isInfoCollected, setIsInfoCollected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (userInfo.name && userInfo.email) {
      setIsInfoCollected(true);
      setMessages([
        {
          id: 1,
          text: `Hello ${userInfo.name}! Welcome to VetForce Solutions. How can I assist you today?`,
          isUser: false,
          timestamp: new Date()
        }
      ]);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/chat`, {
        session_id: sessionId,
        name: userInfo.name,
        email: userInfo.email,
        message: inputValue
      });

      const botMessage = {
        id: Date.now() + 1,
        text: response.data.response,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, there was an error. Please try again or call us at 407-777-2216.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      {/* Chat Bubble */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="chat-bubble"
            data-testid="chat-bubble"
          >
            <MessageCircle className="w-5 h-5 text-silver-shine" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="chat-window"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="px-4 py-3 flex items-center justify-between border-b border-white/5"
              style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}>
              <div>
                <h3 className="text-silver-shine font-semibold text-sm">VetForce Support</h3>
                <p className="text-silver-shine/60 text-xs">We reply within minutes</p>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-silver-shine/70 hover:text-silver-shine transition-colors p-1"
                data-testid="close-chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="h-64 overflow-y-auto p-3 space-y-3" style={{ background: '#0d1117' }}>
              {!isInfoCollected ? (
                <form onSubmit={handleInfoSubmit} className="space-y-3">
                  <p className="text-slate-400 text-xs">
                    Please provide your information to start chatting.
                  </p>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={userInfo.name}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, name: e.target.value }))}
                    className="form-input"
                    required
                    data-testid="chat-name-input"
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="form-input"
                    required
                    data-testid="chat-email-input"
                  />
                  <button 
                    type="submit" 
                    className="btn-primary w-full py-2 text-sm"
                    data-testid="chat-start-btn"
                  >
                    Start Chat
                  </button>
                </form>
              ) : (
                <>
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                          msg.isUser
                            ? 'bg-blue-600 text-silver-shine'
                            : 'bg-white/5 text-slate-300 border border-white/5'
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white/5 text-slate-500 border border-white/5 rounded-lg px-3 py-2 text-xs">
                        <span className="animate-pulse">Typing...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input */}
            {isInfoCollected && (
              <form onSubmit={handleSendMessage} className="p-3 flex gap-2 border-t border-white/5"
                style={{ background: 'rgba(13, 17, 23, 0.95)' }}>
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="flex-1 bg-white/5 border border-white/5 rounded-lg px-3 py-2 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-blue-500/40"
                  disabled={isLoading}
                  data-testid="chat-message-input"
                />
                <button 
                  type="submit" 
                  className="btn-primary p-2 rounded-lg"
                  disabled={isLoading || !inputValue.trim()}
                  data-testid="chat-send-btn"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
